/**
 * DiscoveryView — "how many steps are you from a notable figure?"
 *
 * Reads /api/discover with the current focal person, lists the closest
 * notables with their connection chain. Works only once the global
 * persons layer has at least one path from the user to a seeded notable.
 */
import { useEffect, useState } from 'react'
import { api } from '../../services/api.js'
import { useTreeStore } from '../../stores/tree.js'

export default function DiscoveryView({ onPersonClick }) {
  const focalPersonId = useTreeStore(s => s.focalPersonId)
  const persons = useTreeStore(s => s.persons)
  const [results, setResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!focalPersonId) { setResults([]); return }
    setLoading(true)
    setError(null)
    api.get(`/discover?person_id=${encodeURIComponent(focalPersonId)}`)
      .then(r => setResults(r.results || []))
      .catch(err => {
        // Two common failure modes at first run: the /api/discover route
        // hasn't been deployed yet, or migrations 013/014 haven't run so the
        // persons.is_notable column is missing. Either way the raw DB error
        // isn't useful to a user — surface a practical hint instead.
        const msg = (err?.message || '').toLowerCase()
        if (msg.includes('is_notable') || msg.includes('tree_relationships') ||
            msg.includes('column') || msg.includes('relation') || msg.includes('does not exist')) {
          setError('Discovery is not ready yet — the platform still needs the latest database migrations and a seeded list of notable figures. Ask an admin to run scripts/seed_notables.js.')
        } else if (msg.includes('404') || msg.includes('not found')) {
          setError('Discovery is not available yet on this server.')
        } else {
          setError("We couldn't build the connection graph this time. Please try again.")
        }
      })
      .finally(() => setLoading(false))
  }, [focalPersonId])

  if (!focalPersonId) {
    return (
      <div className="empty-state" style={{ padding: '3rem 1rem' }}>
        <h3>Pick a focal person first</h3>
        <p>Open someone on the Family Tree, then come back here to see who you're connected to.</p>
      </div>
    )
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '760px', margin: '0 auto' }}>
      <h2 style={{ fontFamily: 'Playfair Display, serif', marginBottom: '0.25rem' }}>
        Who you're connected to
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '2rem' }}>
        The shortest chains from this person to notable figures in the
        platform. Connections emerge as more families import and link
        their trees.
      </p>

      {loading && <div className="loading">Searching the graph…</div>}

      {error && (
        <div className="form-error">{error}</div>
      )}

      {!loading && results && results.length === 0 && (
        <div className="empty-state" style={{ padding: '3rem 1rem' }}>
          <h3>No connections yet</h3>
          <p>
            Add more ancestors or import a wider GEDCOM to grow the
            graph. We search up to 10 degrees of separation.
          </p>
        </div>
      )}

      {!loading && results && results.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {results.map(r => (
            <DiscoveryCard
              key={r.person.id}
              item={r}
              personsById={new Map(persons.map(p => [p.id, p]))}
              onPersonClick={onPersonClick}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function DiscoveryCard({ item, personsById, onPersonClick }) {
  const { person, distance, chain } = item

  return (
    <div style={{
      background: 'white', border: '1px solid var(--border)',
      borderRadius: '10px', padding: '1rem 1.25rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '1rem' }}>{person.display_name}</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '2px' }}>
            {[person.category, person.birth_year && `b. ${person.birth_year}`, person.death_year && `d. ${person.death_year}`]
              .filter(Boolean).join(' · ')}
          </div>
        </div>
        <div style={{
          fontSize: '0.78rem', fontWeight: 600, color: 'var(--green)',
          background: 'color-mix(in srgb, var(--green) 10%, white)',
          padding: '0.25rem 0.6rem', borderRadius: '999px',
        }}>
          {distance} step{distance === 1 ? '' : 's'}
        </div>
      </div>
      {chain && chain.length > 0 && (
        <div style={{ marginTop: '0.6rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          {chain.map((pid, i) => {
            const p = personsById.get(pid)
            const label = p
              ? [p.preferred_name || p.first_name, p.last_name].filter(Boolean).join(' ')
              : (pid === person.id ? person.display_name : 'Unknown')
            return (
              <span key={pid}>
                {i > 0 && <span style={{ margin: '0 0.3rem', opacity: 0.6 }}>→</span>}
                <button
                  onClick={() => onPersonClick && onPersonClick(pid)}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    color: 'inherit', cursor: onPersonClick ? 'pointer' : 'default',
                    textDecoration: onPersonClick ? 'underline dotted' : 'none',
                  }}
                >
                  {label}
                </button>
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}
